import { useMutation, useQuery } from '@tanstack/react-query';
import { API_ENDPOINTS } from 'constants/endpoints';
import { del, get, handleError, handleResponse, post, put } from './base.service';
import toast from 'react-hot-toast';
import { updateUserInfo } from 'store/auth';

export const useUserDetailsQuery = (id: any) =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(`${API_ENDPOINTS.USER_DETAILS}/${id}`);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['userDetails'],
		// enabled: !!id,
	});

export const useUpdateUserDetailsMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return put(API_ENDPOINTS.USER_UPDATE_PROFILE, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useGetPreferencesFieldsQuery = () =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(`${API_ENDPOINTS.USER_PREFERENCES_FIELDS}`);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		refetchOnWindowFocus: false,
		queryKey: ['user-preferences-fields'],
	});

export const useSavePreferencesMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return post(API_ENDPOINTS.USER_PREFERENCES_SAVE, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useGetPreferencesQuery = () =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(`${API_ENDPOINTS.USER_GET_PREFERENCES}`);
				return response;
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['get-user-preferences'],
		refetchOnWindowFocus: false,
	});

export const useChangePasswordMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return post(API_ENDPOINTS.USER_CHANGE_PASSWORD, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useDeleteMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return post(API_ENDPOINTS.USER_DELETE_ACCOUNT, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useDeactivateMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return post(API_ENDPOINTS.USER_DEACTIVATE_ACCOUNT, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useChange2FAStatusMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return post(API_ENDPOINTS.USER_CHANGE_2FA, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useVerify2FAStatusMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return post(API_ENDPOINTS.USER_VERIFY_2FA, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useSendTFAQuery = (payload: any) =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await post(`${API_ENDPOINTS.AUTH_SEND_2FA}`, payload);
				toast.success(response?.message);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['send-2fa-otp'],
		staleTime: Infinity,
		enabled: !!payload?.userId && !!payload?.userType,
		refetchOnWindowFocus: false,
	});

export const useVerify2faOtpMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return post(API_ENDPOINTS.AUTH_VERIFY_2FA, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useVerifyMobileMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return put(API_ENDPOINTS.USER_VERIFY_PROFILE_MOBILE, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useVerifyProfileMobileMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return put(API_ENDPOINTS.USER_SEND_PROFILE_OTP, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useSendNumberOtpQuery = (payload: any = {}) =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await post(`${API_ENDPOINTS.USER_SEND_PROFILE_OTP}`, payload);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['send-profile-otp'],
		enabled: !!payload?.phoneNumber,
	});

export const useChange2FAStatusQuery = (payload: any = {}) =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await post(`${API_ENDPOINTS.USER_CHANGE_2FA}`, payload);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['send-2fa-otp'],
		enabled: !!payload?.set2fa,
	});

export const useDeleteProfileElementMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return put(API_ENDPOINTS.USER_DELETE_PROFILE_ELEMENT, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useSendConnectRequestMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return post(API_ENDPOINTS.USER_SEND_CONNECTION_REQUEST, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useRemoveConnectRequestMutation = () =>
	useMutation({
		mutationFn: (id: any) => {
			return del(`${API_ENDPOINTS.USER_REMOVE_CONNECTION}/${id}`);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useAcceptConnectionRequestMutation = () =>
	useMutation({
		mutationFn: (id: any) => {
			return put(`${API_ENDPOINTS.USER_ACCEPT_CONNECTION}`, { id });
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useGetMyConnectionListQuery = (payload: any) =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(
					`${API_ENDPOINTS.USER_GET_CONNECTION}?keyword=${payload?.keyword}&order=${payload.order}&page=${payload.currentPage}&limit=${payload?.limit}&connectionType=${payload.connectionType}&userId=${payload?.userId || ''}`,
				);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: [`get-my-connection-${payload.connectionType}`],
		refetchOnWindowFocus: false,
		enabled: !['blockedusers'].includes(payload.connectionType),
	});

export const useGetBlockListQuery = (payload: any) =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(
					`${API_ENDPOINTS.USER_GET_BLOCK_USER_LIST}?order=${payload.order}&page=${payload.currentPage}&limit=${payload?.limit}&keyword=${payload?.keyword}&userId=${payload?.userId || ''}`,
				);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['get-block-users'],
		refetchOnWindowFocus: false,
		enabled: ['blockedusers', 'blockedProfiles'].includes(payload.connectionType),
	});

export const useBlockUserMutation = () =>
	useMutation({
		mutationFn: (id: any) => {
			return put(`${API_ENDPOINTS.USER_BLOCK_USER}/${id}`);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useUnblockUserMutation = () =>
	useMutation({
		mutationFn: (id: any) => {
			return put(`${API_ENDPOINTS.USER_UNBLOCK_USER}/${id}`);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useGetFollowingHashtagQuery = (payload: any) =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(
					`${API_ENDPOINTS.FOLLOW_HASHTAG}?order=${payload.order}&page=${payload.currentPage}&limit=${payload?.limit}&keyword=${payload?.keyword}&userId=${payload?.id || ''}`,
				);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['following-hashtags'],
		refetchOnWindowFocus: true,
		// enabled: ['hashtag'].includes(payload.connectionType),
	});

export const useGetFollowingCompanyQuery = (payload: any) =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(
					`${API_ENDPOINTS.USER_FOLLOWINGS}/${payload?.id}?order=${payload.order}&page=${payload.currentPage}&limit=${payload?.limit}&keyword=${payload?.keyword}`,
				);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['following-company'],
		refetchOnWindowFocus: false,
		// enabled: ['company'].includes(payload.connectionType),
	});

export const useGetFollowersCompanyQuery = (payload: any) =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(
					`${API_ENDPOINTS.USER_FOLLOWERS}/${payload?.id}?order=${payload.order}&page=${payload.currentPage}&limit=${payload?.limit}&keyword=${payload?.keyword}`,
				);
				if (response?.success) {
					updateUserInfo({ followers: response?.data?.meta?.totalDocs });
				}
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['followers-company'],
		refetchOnWindowFocus: false,
		// enabled: ['followers'].includes(payload.connectionType),
	});

export const useGetFollowersOtherCompanyQuery = (payload: any) =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(
					`${API_ENDPOINTS.USER_FOLLOWERS}/${payload?.id}?order=${payload.order}&page=${payload.currentPage}&limit=${payload?.limit}&keyword=${payload?.keyword}`,
				);

				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['followers-other-company'],
		refetchOnWindowFocus: false,
		// enabled: ['followers'].includes(payload.connectionType),
	});

export const useGetEmployeeListQuery = (payload: any) =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(
					`${API_ENDPOINTS.COMPANY_EMPLOYEE_LIST}?order=${payload.order}&page=${payload.currentPage}&limit=${payload?.limit}&keyword=${payload?.keyword}&userId=${payload?.id || ''}`,
				);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['employee-list'],
		refetchOnWindowFocus: false,
	});

export const useNotificationRemoveConnectRequestMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return del(
				`${API_ENDPOINTS.USER_REMOVE_CONNECTION}/${payload.id}?notificationId=${payload?.notificationId}`,
			);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useNotificationAcceptConnectionRequestMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return put(`${API_ENDPOINTS.USER_ACCEPT_CONNECTION}`, {
				id: payload?.id,
				notificationId: payload?.notificationId,
			});
		},
		onSuccess: handleResponse,
		onError: handleError,
	});
